// Help (customers) and Support (admins): the API calls, labels and formatting
// both pages share. The rules themselves live in backend/support_rules.py.
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from './api';
import { getRegionConfig } from './regionConfig';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

export const supportApi = {
  // Customer
  myThreads: () => axios.get(`${API_URL}/support/threads`, auth()).then((r) => r.data),
  myThread: (id) => axios.get(`${API_URL}/support/threads/${id}`, auth()).then((r) => r.data),
  start: (body) => axios.post(`${API_URL}/support/threads`, body, auth()).then((r) => r.data),
  reply: (id, message) =>
    axios.post(`${API_URL}/support/threads/${id}/messages`, { message }, auth()).then((r) => r.data),
  myUnread: () => axios.get(`${API_URL}/support/unread`, auth()).then((r) => r.data.count),
  upload: (id, files, message) =>
    axios.post(`${API_URL}/support/threads/${id}/files`, filesForm(files, { message }), auth()).then((r) => r.data),
  myFileLink: (fileId) => axios.get(`${API_URL}/support/files/${fileId}/link`, auth()).then((r) => r.data.url),
  myOnboarding: () => axios.get(`${API_URL}/support/onboarding`, auth()).then((r) => r.data),

  // Admin
  threads: (view, q) =>
    axios.get(`${API_URL}/admin/support/threads`, { ...auth(), params: { view, q: q || undefined } }).then((r) => r.data),
  thread: (id) => axios.get(`${API_URL}/admin/support/threads/${id}`, auth()).then((r) => r.data),
  adminReply: (id, body) =>
    axios.post(`${API_URL}/admin/support/threads/${id}/messages`, body, auth()).then((r) => r.data),
  setStatus: (id, status) =>
    axios.patch(`${API_URL}/admin/support/threads/${id}`, { status }, auth()).then((r) => r.data),
  adminStart: (body) => axios.post(`${API_URL}/admin/support/threads`, body, auth()).then((r) => r.data),
  counts: () => axios.get(`${API_URL}/admin/support/counts`, auth()).then((r) => r.data),
  adminUpload: (id, files, message, status) =>
    axios.post(`${API_URL}/admin/support/threads/${id}/files`, filesForm(files, { message, status }), auth())
      .then((r) => r.data),
  fileLink: (fileId) => axios.get(`${API_URL}/admin/support/files/${fileId}/link`, auth()).then((r) => r.data.url),
  setStage: (id, stage) =>
    axios.patch(`${API_URL}/admin/support/threads/${id}/stage`, { stage }, auth()).then((r) => r.data),
  agentToken: (id) => axios.post(`${API_URL}/admin/support/threads/${id}/agent-token`, {}, auth()).then((r) => r.data),

  // Account access. Opening and leaving switch the session cookie too, so
  // they send credentials (the API is another origin in local development).
  openAccount: (id) =>
    axios.post(`${API_URL}/admin/support/threads/${id}/open-account`, {}, { ...auth(), withCredentials: true })
      .then((r) => r.data),
  accessNow: () => axios.get(`${API_URL}/account-access`, auth()).then((r) => r.data),
  endAccess: () => axios.post(`${API_URL}/account-access/end`, {}, { ...auth(), withCredentials: true }).then((r) => r.data),
  stopAccess: () => axios.post(`${API_URL}/support/onboarding/stop-access`, {}, auth()).then((r) => r.data),
  completeOnboarding: () => axios.post(`${API_URL}/support/onboarding/complete`, {}, auth()).then((r) => r.data),
};

function filesForm(files, fields) {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  Object.entries(fields).forEach(([k, v]) => { if (v) form.append(k, v); });
  return form;
}

// What can be attached: the same list as ALLOWED_FILES in support_rules.py,
// for the picker. The server decides; this only filters the dialog.
export const ATTACH_ACCEPT =
  '.csv,.xlsx,.xls,.numbers,.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.heic,.heif,.webp,.zip';

/**
 * Download a file from a short-lived link without opening a tab.
 *
 * The link is fetched after the click, and a browser blocks a new window
 * opened that late as an unasked-for pop-up. Every file is served with
 * Content-Disposition: attachment (file_store.py), so following the link in
 * this page saves the file and leaves the page where it is.
 */
export function downloadFromLink(url) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Onboarding's stages, in order (STAGES in support_rules.py)
export const ONBOARDING_STAGES = [
  { value: 'booked', label: 'Booked', customer: 'Send us your gear list' },
  { value: 'files_received', label: 'Files received', customer: 'We have your files' },
  { value: 'importing', label: 'Importing', customer: 'We\u2019re importing your gear' },
  { value: 'ready', label: 'Ready to review', customer: 'Have a look over your gear' },
  { value: 'done', label: 'Done', customer: 'All done' },
];

/** The error the API gave, in its own words, or a fallback. */
export function apiError(error, fallback) {
  return error?.response?.data?.detail || fallback;
}

/**
 * Show an API error, except "not authenticated": that means the session has
 * ended (signed out, or it expired) and the app is already on its way to the
 * login page. Pages still open at that moment each used to say so, at once.
 */
export function toastApiError(error, fallback) {
  if (error?.response?.status === 401) return;
  toast.error(apiError(error, fallback));
}

// What a customer can choose when they start one (onboarding is opened for them)
export const CUSTOMER_KINDS = [
  { value: 'question', label: 'A question' },
  { value: 'problem', label: 'Something isn’t working' },
  { value: 'idea', label: 'An idea' },
  { value: 'billing', label: 'Billing' },
];

export const ADMIN_KINDS = [
  { value: 'question', label: 'Question' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'billing', label: 'Billing' },
  { value: 'problem', label: 'Problem' },
];

// Status as each side reads it. Told apart by fill as well as words,
// never by colour alone (DESIGN.md).
const STATUS = {
  open: { customer: 'With us', admin: 'Needs reply', className: 'bg-ochre-soft text-ochre' },
  waiting_on_customer: { customer: 'Replied', admin: 'Waiting on them', className: 'bg-mist text-foreground' },
  closed: { customer: 'Closed', admin: 'Closed', className: 'border border-line-strong bg-transparent text-muted-foreground' },
};

export function statusLabel(status, side) {
  return STATUS[status]?.[side] || status;
}

export function statusClass(status) {
  return STATUS[status]?.className || STATUS.closed.className;
}

/** "23 Oct, 9:41 am" in the viewer's region; the year only when it isn't this one. */
export function formatMessageTime(iso, region) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const locale = getRegionConfig(region).dateFormat.locale;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** The page name a conversation was started from, for context. */
export function currentPageName(pathname) {
  const first = pathname.split('/').filter(Boolean)[0] || 'dashboard';
  return first.charAt(0).toUpperCase() + first.slice(1);
}

// A plan's name as admins read it (SUBSCRIPTION_PLANS in plans.py, plus the trial and demo)
const PLAN_NAMES = {
  free_trial: 'Free trial', founding_member: 'Solo', regular: 'Regular', projects: 'Filmmaker', producer: 'Teams', demo: 'Demo',
};

export function planName(plan) {
  return PLAN_NAMES[plan] || plan || '-';
}
